import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Suspense } from 'react';
import { preloadRoute } from '@/lib/lazyPage';
import { PageSkeleton } from '@/components/perf/PerfUI';
import EquipmentSignPopup from '@/components/EquipmentSignPopup';
import { Button } from '@/components/ui/button';
import ProfilePhotoPrompt from '@/components/ProfilePhotoPrompt';
import { Avatar } from '@/components/Avatar';
import { 
  LayoutDashboard, Users, History, BarChart3, Download, LogOut,
  Store, UserCog, CalendarOff, ShieldCheck, Calculator, Key, Activity,
  Menu, X, ClipboardCheck, Bell, BellOff, ListChecks, MessageSquare, Settings, AlertTriangle, CalendarDays, Banknote, ArrowLeft, Crown, FileText, Building2, CreditCard, Link2, Copy, FileSignature, Inbox, ClipboardList, Truck, ShoppingCart, BookOpen, Film, Gem, Camera, MapPin, Palette, Loader2
} from 'lucide-react';
import { Gamepad2, SlidersHorizontal, Radio, Star, Shirt, ChevronDown, Sun, Moon, Trophy, Briefcase, Boxes, Route, Receipt, Landmark } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '@/hooks/useAuth';
import { userHasCapability, CASH_CAPABILITIES } from '@/hooks/usePermissions';
import { useAdminStorePlan } from '@/hooks/useAdminStorePlan';
import { canAccessGestionnairePath } from '@/lib/gestionnaireAccess';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import NotificationBell from '@/components/NotificationBell';
import { toast } from 'sonner';

// Iter 446 : familles de navigation (gerant/responsable) — menu groupe + hub dashboard
import { NAV_FAMILIES, MobileBottomNav } from '@/components/navigation/NavPlan';
import { BackBar, HistoryGuards } from '@/components/navigation/BackBar';
import { Fingerprint, HardDriveDownload } from 'lucide-react';
import { useResponsiveTables } from '@/hooks/useResponsiveTables';
export { NAV_FAMILIES };

// Iter 443 : wrapper drag & drop pour reordonner les favoris du menu
const SortableFavItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.65 : 1, position: 'relative', zIndex: isDragging ? 50 : 'auto' }}
      data-testid={`fav-sortable${id.replace(/\//g, '-')}`}>
      {children}
    </div>
  );
};
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ===== LOGO: stored as compressed data URL in localStorage =====
function getSidebarLogo() {
  // Hyperadmin always sees Logyra logo
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (u?.role === 'hyperadmin') return '/logos/logyra-rh-sans-fond.png';
  } catch {}
  const dataUrl = localStorage.getItem('enseigne_logo_data');
  if (dataUrl) return dataUrl;
  return '/logos/logyra-rh-sans-fond.png';
}

function compressAndStoreLogoData(imgSrc) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const maxW = 200;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Use PNG to preserve transparency
        const dataUrl = canvas.toDataURL('image/png');
        try { localStorage.setItem('enseigne_logo_data', dataUrl); } catch {}
        resolve(dataUrl);
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = imgSrc;
  });
}

const SidebarLogo = ({ height = 'h-14', logoVersion }) => {
  const src = getSidebarLogo();
  return <img src={src} alt="" className={`${height} object-contain`} />;
};

const AdminLayout = ({ children }) => {
  useResponsiveTables();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState({ unread_messages: 0, pending_leaves: 0, pending_overtime: 0, pending_regularizations: 0 });
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe } = usePushNotifications();
  const { storePlan, planLoaded, planError, retryPlan } = useAdminStorePlan(user);
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);
  const [lockedTierInfo, setLockedTierInfo] = useState('standard'); // 'standard' or 'premium'
  const [logoVersion, setLogoVersion] = useState(0);
  const [hasColorRotationStore, setHasColorRotationStore] = useState(false);
  const [enseigneSlug, setEnseigneSlug] = useState(() => {
    try { return JSON.parse(localStorage.getItem('enseigne_cache') || '{}').slug || null; } catch { return null; }
  });
  // Iter 439 : radio de la centrale (URL par enseigne, ouvre un nouvel onglet)
  const [radioUrl, setRadioUrl] = useState(() => {
    try { return JSON.parse(localStorage.getItem('enseigne_cache') || '{}').radio_url || null; } catch { return null; }
  });

  // Iter 439 : le login ecrit enseigne_cache en arriere-plan APRES le montage
  // du layout (guard __enseigneFetched) -> on se resynchronise via cet event.
  useEffect(() => {
    const sync = () => {
      try {
        const c = JSON.parse(localStorage.getItem('enseigne_cache') || '{}');
        if (c.slug) setEnseigneSlug(c.slug);
        setRadioUrl(c.radio_url || null);
      } catch {}
    };
    window.addEventListener('enseigne-cache-updated', sync);
    return () => window.removeEventListener('enseigne-cache-updated', sync);
  }, []);

  // Fetch enseigne ONCE per session - download logo as data URL for instant display
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (window.__enseigneFetched) return;
    window.__enseigneFetched = true;
    
    (async () => {
      // Hyperadmin: ALWAYS force Logyra logo and clear any enseigne cache
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.role === 'hyperadmin') {
          localStorage.removeItem('enseigne_cache');
          localStorage.removeItem('enseigne_logo_data');
          await compressAndStoreLogoData('/logos/logyra-rh-sans-fond.png');
          setLogoVersion(v => v + 1);
          return;
        }
      } catch {}
    
      try {
        const res = await fetch(`${BACKEND_URL}/api/v2/my-enseigne`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.enseigne) {
            localStorage.setItem('enseigne_cache', JSON.stringify(data.enseigne));
            if (data.enseigne.slug) setEnseigneSlug(data.enseigne.slug);
            setRadioUrl(data.enseigne.radio_url || null);
            if (data.enseigne.logo_url) {
              await compressAndStoreLogoData(`${BACKEND_URL}${data.enseigne.logo_url}`);
              setLogoVersion(v => v + 1);
            }
          } else {
            // No enseigne: use Logyra default
            localStorage.removeItem('enseigne_logo_data');
            setLogoVersion(v => v + 1);
          }
        }
      } catch {}
    })();
  }, []); // eslint-disable-line

  // Iter 216: Detect if at least one store has color_rotation_mode active
  useEffect(() => {
    if (!user || user.role === 'hyperadmin') return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${BACKEND_URL}/api/v2/my-stores`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const stores = await r.json();
          setHasColorRotationStore((stores || []).some(s => s.color_rotation_mode));
        }
      } catch {}
    })();
  }, [user]);

  // Iter 422 : Logyra Quest visible seulement si active par l'hyperadmin
  const [gameEnabled, setGameEnabled] = useState(false);
  // Iter 545 : onglet Chiffre d'affaires pour un responsable seulement si le gerant l'a coche
  const [revenueAccess, setRevenueAccess] = useState('none');
  useEffect(() => {
    if (!user || user.role !== 'store_admin') return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${BACKEND_URL}/api/revenue/my-access`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setRevenueAccess((await r.json()).access || 'none');
      } catch {}
    })();
  }, [user]);
  useEffect(() => {
    if (!user || user.role === 'hyperadmin') return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${BACKEND_URL}/api/v2/game/settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setGameEnabled(!!(await r.json()).enabled_for_all);
      } catch {}
    })();
  }, [user]);

  // Redirect based on subscription plan - ONLY after plan is fetched from server
  useEffect(() => {
    if (!user || user.role === 'hyperadmin' || user.is_gestionnaire || !planLoaded) return;
    if (storePlan === 'none') {
      if (user.role === 'super_admin') {
        // Gérant: redirect to subscription page (they can subscribe)
        const allowedPaths = ['/admin/subscription', '/admin/profile'];
        if (!allowedPaths.includes(location.pathname)) {
          navigate('/admin/subscription', { replace: true });
        }
      } else if (user.role === 'store_admin') {
        // Responsable: redirect to profile (can't subscribe, just info)
        if (location.pathname !== '/admin/profile' && location.pathname !== '/admin/no-subscription') {
          navigate('/admin/no-subscription', { replace: true });
        }
      }
    } else {
      // Has subscription: if on subscription or no-subscription page and not intentionally viewing, redirect to dashboard
      if (location.pathname === '/admin/no-subscription') {
        navigate('/admin/dashboard', { replace: true });
      }
      if (storePlan === 'standard') {
        const premiumOnlyPaths = ['/admin/costs', '/admin/stats', '/admin/my-payslips', '/admin/contracts', '/admin/documents', '/admin/messages'];
        if (premiumOnlyPaths.includes(location.pathname)) {
          navigate('/admin/dashboard', { replace: true });
        }
      }
      if (storePlan === 'standard' || storePlan === 'premium') {
        const diamondOnlyPaths = ['/admin/tasks', '/admin/deliveries', '/admin/orders', '/admin/game', '/admin/merchandise', '/admin/transport', '/admin/inventory'];
        if (diamondOnlyPaths.includes(location.pathname)) {
          navigate('/admin/dashboard', { replace: true });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storePlan, location.pathname, user?.id, user?.role, user?.is_gestionnaire, navigate, planLoaded]);

  // Fetch badge counts
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/admin/sidebar-badges`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setBadges(await res.json());
      } catch {}
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 15000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  // Auto-prompt for push on first admin login
  useEffect(() => {
    if (isSupported && !isSubscribed && permission === 'default' && user) {
      const prompted = localStorage.getItem('push_prompted');
      if (!prompted) {
        localStorage.setItem('push_prompted', '1');
        const timer = setTimeout(() => {
          subscribe().then(ok => {
            if (ok) toast.success('Notifications push activees !');
          });
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [isSupported, isSubscribed, permission, user]);

  const togglePush = async () => {
    if (isSubscribed) {
      await unsubscribe();
      toast.success('Notifications push desactivees');
    } else {
      const ok = await subscribe();
      if (ok) toast.success('Notifications push activees !');
      else toast.error('Impossible d\'activer les notifications');
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch {}
    logout();
    navigate('/login');
  };

  const hyperAdminItems = [
    { path: '/admin/enseignes', icon: Building2, label: 'Enseignes' },
    { path: '/admin/enseigne-requests', icon: Inbox, label: 'Demandes inscription' },
    { path: '/admin/stores', icon: Store, label: 'Magasins' },
    { path: '/admin/gerants', icon: Crown, label: 'Gerants' },
    { path: '/admin/store-admins', icon: UserCog, label: 'Responsables' },
    { path: '/admin/comptables', icon: Calculator, label: 'Comptables' },
    { path: '/admin/gestionnaires', icon: Briefcase, label: 'Gestionnaires' },
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/live', icon: Activity, label: 'Temps reel' },
    { path: '/admin/employees', icon: Users, label: 'Employe(e)s' },
    { path: '/admin/equipements', icon: Shirt, label: 'Equipements' },
    { path: '/admin/messages', icon: MessageSquare, label: 'Messages' },
    { path: '/admin/caisse', icon: Banknote, label: 'Caisse & Coffre' },
    { path: '/admin/banque', icon: Landmark, label: 'Banque' },
    { path: '/admin/clotures-de-caisse', icon: ClipboardCheck, label: 'Clôtures de caisse' },
    { path: '/admin/mandats', icon: Landmark, label: 'Mandats administratifs' },
    { path: '/admin/game', icon: Gamepad2, label: 'Logyra Quest' },
    { path: '/admin/ha-invoices', icon: FileText, label: 'Factures abonnements' },
    { path: '/admin/facturation', icon: Receipt, label: 'Facturation' },
    { path: '/admin/ha-sales-stats', icon: BarChart3, label: 'Stats ventes' },
    { path: '/admin/revenue', icon: Trophy, label: "Chiffre d'affaires" },
    { path: '/admin/photo-attempts', icon: Camera, label: 'Photos profil' },
    { path: '/admin/photo-migration', icon: HardDriveDownload, label: 'Maintenance' },
    { path: '/admin/sessions', icon: Activity, label: 'Connexions' },
    { path: '/admin/contracts', icon: FileSignature, label: 'Contrats' },
    { path: '/admin/demo-requests', icon: Inbox, label: 'Demandes demo' },
    { path: '/admin/tutorial-studio', icon: Film, label: 'Studio tutoriels' },
    { path: '/admin/credentials', icon: Key, label: 'Identifiants' },
    { path: '/admin/camera-test', icon: Camera, label: 'Test camera' },
    { path: '/admin/notification-settings', icon: Settings, label: 'Notifications' }
  ];

  // Premium-only paths (for Standard plan restriction)
  const PREMIUM_PATHS = ['/admin/costs', '/admin/stats', '/admin/my-payslips', '/admin/contracts', '/admin/documents'];

  // Tier system: 'standard' = needs at least Standard, 'premium' = needs Premium
  // Items without tier are always accessible (but gérant without sub only sees bottom section)
  const superAdminItems = [
    { path: '/admin/stores', icon: Store, label: 'Magasins', tier: 'standard' },
    { path: '/admin/store-admins', icon: UserCog, label: 'Responsables', tier: 'standard' },
    { path: '/admin/comptables', icon: Calculator, label: 'Comptables', tier: 'standard' },
    { path: '/admin/responsable-leaves', icon: CalendarOff, label: 'Congés responsable', tier: 'standard' },
    { path: '/admin/my-payslips', icon: FileText, label: 'Bulletin de salaire', tier: 'premium' },
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', tier: 'standard' },
    { path: '/admin/live', icon: Activity, label: 'Temps reel', tier: 'standard' },
    { path: '/admin/revenue', icon: Trophy, label: "Chiffre d'affaires", tier: 'standard' },
    { path: '/admin/employees', icon: Users, label: 'Employe(e)s', tier: 'standard' },
    { path: '/admin/equipements', icon: Shirt, label: 'Equipements', tier: 'standard' },
    { path: '/admin/tasks', icon: ClipboardList, label: 'Taches', tier: 'diamond' },
    { path: '/admin/deliveries', icon: Truck, label: 'Livraisons', tier: 'diamond' },
    { path: '/admin/merchandise', icon: Truck, label: 'Transferts marchandise', tier: 'diamond' },
    { path: '/admin/transport', icon: Route, label: 'Transport', tier: 'diamond' },
    { path: '/admin/inventory', icon: Boxes, label: 'Inventaire', tier: 'diamond' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Commandes', tier: 'diamond' },
    { path: '/admin/suppliers', icon: Building2, label: 'Fournisseurs', tier: 'standard' },
    { path: '/admin/schedules', icon: CalendarDays, label: 'Plannings', tier: 'standard' },
    { path: '/admin/color-rotation-planning', icon: Palette, label: 'Planning couleur', tier: 'standard', requiresColorRotation: true, hidden: true },
    { path: '/admin/absences', icon: CalendarOff, label: 'Absences', tier: 'standard' },
    { path: '/admin/messages', icon: MessageSquare, label: 'Messages', tier: 'premium' },
    { path: '/admin/pointages', icon: Fingerprint, label: 'Pointages', badgeKeys: ['pending_regularizations', 'pending_overtime'] },
    { path: '/admin/caisse', icon: Banknote, label: 'Caisse & Coffre', tier: 'diamond' },
    { path: '/admin/banque', icon: Landmark, label: 'Banque', tier: 'diamond' },
    { path: '/admin/clotures-de-caisse', icon: ClipboardCheck, label: 'Clôtures de caisse', tier: 'diamond' },
    { path: '/admin/mandats', icon: Landmark, label: 'Mandats administratifs', tier: 'diamond' },
    { path: '/admin/facturation', icon: Receipt, label: 'Facturation', tier: 'standard' },
    { path: '/admin/game', icon: Gamepad2, label: 'Logyra Quest', tier: 'diamond', requiresGameEnabled: true },
    { path: '/admin/recap-mensuel', icon: FileText, label: 'Recap mensuel', tier: 'standard' },
    { path: '/admin/costs', icon: Banknote, label: 'Couts personnel', tier: 'premium' },
    { path: '/admin/stats', icon: BarChart3, label: 'Statistiques', tier: 'premium' },
    { path: '/admin/exports', icon: Download, label: 'Exports', tier: 'standard' },
    { path: '/admin/documents', icon: FileText, label: 'Contrats & Documents', tier: 'premium' },
    { path: '/admin/notification-settings', icon: Settings, label: 'Notifications', tier: 'standard' },
    { path: '/admin/tutorials', icon: BookOpen, label: 'Tutoriels', tier: 'standard' }
  ];

  const storeAdminItems = [
    { path: '/admin/my-stores', icon: Store, label: 'Magasins', tier: 'standard' },
    { path: '/admin/responsable-leaves', icon: CalendarOff, label: 'Congés responsable', tier: 'standard' },
    { path: '/admin/my-payslips', icon: FileText, label: 'Bulletin de salaire', tier: 'premium' },
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', tier: 'standard' },
    { path: '/admin/live', icon: Activity, label: 'Temps reel', tier: 'standard' },
    { path: '/admin/employees', icon: Users, label: 'Employe(e)s', tier: 'standard' },
    { path: '/admin/equipements', icon: Shirt, label: 'Equipements', tier: 'standard' },
    { path: '/admin/tasks', icon: ClipboardList, label: 'Taches', tier: 'diamond' },
    { path: '/admin/deliveries', icon: Truck, label: 'Livraisons', tier: 'diamond' },
    { path: '/admin/merchandise', icon: Truck, label: 'Transferts marchandise', tier: 'diamond' },
    { path: '/admin/transport', icon: Route, label: 'Transport', tier: 'diamond' },
    { path: '/admin/inventory', icon: Boxes, label: 'Inventaire', tier: 'diamond' },
    { path: '/admin/revenue', icon: Trophy, label: "Chiffre d'affaires", tier: 'standard', requiresRevenueAccess: true },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Commandes', tier: 'diamond' },
    { path: '/admin/suppliers', icon: Building2, label: 'Fournisseurs', tier: 'standard' },
    { path: '/admin/schedules', icon: CalendarDays, label: 'Plannings', tier: 'standard' },
    { path: '/admin/color-rotation-planning', icon: Palette, label: 'Planning couleur', tier: 'standard', requiresColorRotation: true, hidden: true },
    { path: '/admin/absences', icon: CalendarOff, label: 'Absences', tier: 'standard' },
    { path: '/admin/messages', icon: MessageSquare, label: 'Messages', tier: 'premium' },
    { path: '/admin/pointages', icon: Fingerprint, label: 'Pointages', badgeKeys: ['pending_regularizations', 'pending_overtime'] },
    { path: '/admin/recap-mensuel', icon: FileText, label: 'Recap mensuel', tier: 'standard', requiresRecapAccess: true },
    { path: '/admin/caisse', icon: Banknote, label: 'Caisse & Coffre', tier: 'diamond', requiresCash: true },
    { path: '/admin/depots-en-compte', icon: Landmark, label: 'Dépôts en compte', tier: 'diamond', requiresCash: true },
    { path: '/admin/clotures-de-caisse', icon: ClipboardCheck, label: 'Clôtures de caisse', tier: 'diamond', requiresCash: true },
    { path: '/admin/mandats', icon: Landmark, label: 'Mandats administratifs', tier: 'diamond', requiresCash: true },
    { path: '/admin/game', icon: Gamepad2, label: 'Logyra Quest', tier: 'diamond', requiresGameEnabled: true },
    { path: '/admin/costs', icon: Banknote, label: 'Couts personnel', tier: 'premium' },
    { path: '/admin/stats', icon: BarChart3, label: 'Statistiques', tier: 'premium' },
    { path: '/admin/exports', icon: Download, label: 'Exports', tier: 'standard' },
    { path: '/admin/notification-settings', icon: Settings, label: 'Notifications', tier: 'standard' },
    { path: '/admin/tutorials', icon: BookOpen, label: 'Tutoriels', tier: 'standard' }
  ];

  const hasAnyCashCap = CASH_CAPABILITIES.some(cap => userHasCapability(user, cap));

  // Iter 432 : l'hyperadmin choisit les onglets visibles (cases a cocher, stocke en local)
  const [hyperHiddenTabs, setHyperHiddenTabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hyper_hidden_tabs') || '[]'); } catch { return []; }
  });
  const [showTabConfig, setShowTabConfig] = useState(false);
  const toggleTabVisibility = (path) => {
    setHyperHiddenTabs(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      localStorage.setItem('hyper_hidden_tabs', JSON.stringify(next));
      return next;
    });
  };

  const navItems = (user?.role === 'hyperadmin' ? hyperAdminItems : user?.role === 'super_admin' ? superAdminItems : storeAdminItems)
    .filter(item => !item.hidden)
    .filter(item => user?.role !== 'hyperadmin' || !hyperHiddenTabs.includes(item.path))
    .filter(item => !item.requiresColorRotation || hasColorRotationStore)
    .filter(item => !item.requiresCash || hasAnyCashCap)
    .filter(item => !item.requiresGameEnabled || gameEnabled)
    .filter(item => !item.requiresRecapAccess || (user?.extra_permissions || []).includes('view_full_monthly_recap'))
    .filter(item => !item.requiresRevenueAccess || revenueAccess !== 'none')
    // Gestionnaire: inherited tabs + additional tabs granted by the hyperadmin.
    .filter(item => canAccessGestionnairePath(user, item.path))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));

  // Iter 442 : favoris par compte admin (etoile sur chaque onglet, section en haut)
  const [favTabs, setFavTabs] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/v2/me/favorite-tabs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setFavTabs(d.favorites || []); })
      .catch(() => {});
  }, []);
  const toggleFavTab = (path) => {
    setFavTabs(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      const token = localStorage.getItem('token');
      fetch(`${BACKEND_URL}/api/v2/me/favorite-tabs`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ favorites: next }),
      }).catch(() => {});
      return next;
    });
  };
  // Iter 443 : les favoris suivent l'ordre choisi par l'admin (drag & drop), le reste est alphabetique
  // Iter 446 : familles depliables dans le menu (gerant/responsable)
  const [openFamilies, setOpenFamilies] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nav_families_open') || '{}'); } catch { return {}; }
  });
  const toggleFamily = (key) => setOpenFamilies(prev => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem('nav_families_open', JSON.stringify(next)); } catch {}
    return next;
  });
  useEffect(() => {
    const fam = NAV_FAMILIES.find(f => f.paths.includes(location.pathname));
    if (fam) setOpenFamilies(prev => (prev[fam.key] ? prev : { ...prev, [fam.key]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const badgeForPath = (p) =>
    p === '/admin/pointages' ? (badges.pending_regularizations || 0) + (badges.pending_overtime || 0) :
    p === '/admin/messages' ? badges.unread_messages :
    p === '/admin/absences' ? badges.pending_leaves :
    p === '/admin/responsable-leaves' ? badges.pending_responsable_leaves :
    p === '/admin/overtime-requests' ? badges.pending_overtime :
    p === '/admin/regularizations' ? badges.pending_regularizations :
    p === '/admin/equipements' ? badges.equipment_transfers_todo : 0;

  // Iter 449/455 : bascule theme clair / sombre (CLAIR par defaut, sombre en option)
  const [uiTheme, setUiTheme] = useState(() => {
    try { return localStorage.getItem('ui_theme') || 'light'; } catch { return 'light'; }
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', uiTheme === 'dark');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggleTheme = () => {
    setUiTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('ui_theme', next); } catch {}
      document.documentElement.classList.toggle('dark', next === 'dark');
      return next;
    });
  };

  const favoriteItems = favTabs.map(p => navItems.find(i => i.path === p)).filter(Boolean).map(i => ({ ...i, isFav: true }));
  // Iter 455 : un favori reste AUSSI visible dans sa famille (duplique, pas deplace)
  const restItems = navItems;
  const favSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  );
  const handleFavDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const order = favoriteItems.map(i => i.path);
    const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id));
    const hiddenFavs = favTabs.filter(p => !order.includes(p));
    const full = [...next, ...hiddenFavs];
    setFavTabs(full);
    const token = localStorage.getItem('token');
    fetch(`${BACKEND_URL}/api/v2/me/favorite-tabs`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ favorites: full }),
    }).catch(() => {});
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'hyperadmin': return 'Hyperadmin';
      case 'super_admin': return user?.is_gestionnaire ? 'Gestionnaire' : 'Gerant';
      case 'store_admin': return 'Responsable magasin';
      default: return role;
    }
  };

  const handleNavClick = () => {
    setSidebarOpen(false);
  };

  const sidebarContent = (
    <>
      <div className="p-4 pb-3 border-b border-white/10 flex items-center justify-between">
        <button
          onClick={() => { navigate('/admin/dashboard'); setSidebarOpen(false); }}
          className="h-14 flex items-center hover:opacity-80 transition-opacity cursor-pointer"
          aria-label="Aller a Temps reel"
          data-testid="sidebar-logo-btn"
        >
          <SidebarLogo height="h-14" logoVersion={logoVersion} />
        </button>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white p-1 hover:bg-white/10 rounded"
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto sidebar-scroll">
        {user?.role === 'hyperadmin' && (
          <div className="mb-1">
            <button
              onClick={() => setShowTabConfig(v => !v)}
              data-testid="tab-config-toggle"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-white/60 hover:text-white hover:bg-white/10 transition"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Personnaliser les onglets
              {hyperHiddenTabs.length > 0 && (
                <span className="ml-auto text-[10px] bg-amber-400/20 text-amber-300 rounded-full px-1.5 py-0.5">{hyperHiddenTabs.length} masqué{hyperHiddenTabs.length > 1 ? 's' : ''}</span>
              )}
            </button>
            {showTabConfig && (
              <div className="mt-1 mb-2 bg-white/5 border border-white/10 rounded-lg p-2 space-y-0.5 max-h-80 overflow-y-auto" data-testid="tab-config-panel">
                <p className="text-[10px] text-white/40 px-2 pb-1">Cochez les onglets à afficher :</p>
                {hyperAdminItems.filter(it => !it.hidden).map(it => (
                  <label key={it.path} className="flex items-center gap-2 px-2 py-1 rounded text-xs text-white/80 hover:bg-white/10 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!hyperHiddenTabs.includes(it.path)}
                      onChange={() => toggleTabVisibility(it.path)}
                      className="accent-emerald-400 w-3.5 h-3.5"
                      data-testid={`tab-config${it.path.replace(/\//g, '-')}`}
                    />
                    {it.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        {favoriteItems.length > 0 && (
          <p className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider font-bold text-yellow-400/80 flex items-center gap-1" data-testid="favorites-heading">
            <Star className="w-3 h-3" style={{ fill: 'currentColor' }} /> Favoris
          </p>
        )}
        {(() => {
          const renderNavItem = (item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const badgeCount = item.path === '/admin/messages' ? badges.unread_messages :
                            item.path === '/admin/absences' ? badges.pending_leaves :
                            item.path === '/admin/responsable-leaves' ? badges.pending_responsable_leaves :
                            item.path === '/admin/overtime-requests' ? badges.pending_overtime :
                            item.path === '/admin/regularizations' ? badges.pending_regularizations : 0;

          // Determine if this item is locked or hidden based on tier vs current plan
          // Hyperadmin bypasses all locks
          // Plan rank: none(0) < standard(1) < premium(2) < diamond(3)
          let isLocked = false;
          let isHidden = false;
          let lockColor = '';
          if (planLoaded && user?.role !== 'hyperadmin' && !user?.is_gestionnaire && item.tier) {
            const PLAN_RANK = { none: 0, standard: 1, premium: 2, diamond: 3 };
            const TIER_RANK = { standard: 1, premium: 2, diamond: 3 };
            const currentRank = PLAN_RANK[storePlan] ?? 0;
            const requiredRank = TIER_RANK[item.tier] ?? 0;
            if (currentRank < requiredRank) {
              if (storePlan === 'none') {
                isLocked = true;
                lockColor = item.tier === 'diamond' ? 'cyan' : item.tier === 'premium' ? 'yellow' : 'blue';
              } else if (user?.role === 'super_admin') {
                // Gérant: locked (can upgrade)
                isLocked = true;
                lockColor = item.tier === 'diamond' ? 'cyan' : 'yellow';
              } else {
                // Responsable / employee: completely hidden
                isHidden = true;
              }
            }
          }

          if (isHidden) return null;

          if (isLocked) {
            return (
              <button
                key={item.path}
                onClick={() => {
                  setLockedTierInfo(lockColor === 'cyan' ? 'diamond' : lockColor === 'yellow' ? 'premium' : 'standard');
                  setPremiumDialogOpen(true);
                }}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all text-sm text-white/30 hover:text-white/40 hover:bg-white/5 cursor-pointer"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}-locked`}
              >
                <div className="flex items-center space-x-2.5">
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </div>
                {lockColor === 'cyan'
                  ? <Gem className="w-3.5 h-3.5 text-cyan-300" />
                  : <Crown className={`w-3.5 h-3.5 ${lockColor === 'yellow' ? 'text-yellow-500' : 'text-blue-400'}`} />}
              </button>
            );
          }

          return (
            <div key={`${item.path}${item.isFav ? '-fav' : ''}`} className="relative group/nav">
            <Link
              to={item.path}
              onClick={handleNavClick}
              onMouseEnter={() => preloadRoute(item.path)}
              onTouchStart={() => preloadRoute(item.path)}
              onFocus={() => preloadRoute(item.path)}
              className={`flex items-center justify-between px-3 py-2.5 pr-9 rounded-xl transition-all text-sm ${
                isActive 
                  ? 'text-white font-semibold' 
                  : 'text-white/70 hover:text-white hover:bg-white/8'
              }`}
              style={isActive ? { background: 'linear-gradient(135deg, #4578A6, #0a1b3d)' } : {}}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}${item.isFav ? '-fav' : ''}`}
            >
              <div className="flex items-center space-x-2.5">
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </div>
              {badgeCount > 0 && (
                <span className="text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1" style={{ background: '#C40050' }} data-testid={`badge-${item.label.toLowerCase()}`}>
                  {badgeCount}
                </span>
              )}
            </Link>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavTab(item.path); }}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded transition-opacity ${favTabs.includes(item.path) ? 'opacity-100 text-yellow-400' : 'opacity-0 group-hover/nav:opacity-100 text-white/40 hover:text-yellow-300'}`}
                title={favTabs.includes(item.path) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                data-testid={`fav-toggle${item.path.replace(/\//g, '-')}${item.isFav ? '-fav' : ''}`}
              >
                <Star className="w-3.5 h-3.5" style={favTabs.includes(item.path) ? { fill: 'currentColor' } : {}} />
              </button>
            </div>
          );
          };
          return (
            <>
              {favoriteItems.length > 0 && (
                <DndContext sensors={favSensors} collisionDetection={closestCenter} onDragEnd={handleFavDragEnd}>
                  <SortableContext items={favoriteItems.map(i => i.path)} strategy={verticalListSortingStrategy}>
                    {favoriteItems.map((item) => (
                      <SortableFavItem key={`${item.path}-fav`} id={item.path}>
                        {renderNavItem(item)}
                      </SortableFavItem>
                    ))}
                  </SortableContext>
                </DndContext>
              )}
              {favoriteItems.length > 0 && <div className="my-2 border-t border-white/10" data-testid="favorites-divider" />}
              {(() => {
                    const famOf = (p) => NAV_FAMILIES.find(f => f.paths.includes(p));
                    // Iter 455 : les items de premier niveau favorises ne sont pas dupliques
                    // (deja juste au-dessus) ; les sous-onglets des familles, eux, restent.
                    const flatTop = restItems.filter(i => ['/admin/dashboard'].includes(i.path) && !favTabs.includes(i.path));
                    const ungrouped = restItems.filter(i => !['/admin/dashboard'].includes(i.path) && !famOf(i.path) && !favTabs.includes(i.path));
                    return (
                      <>
                        {flatTop.map(i => renderNavItem(i))}
                        {NAV_FAMILIES.map(fam => {
                          const children = restItems.filter(i => fam.paths.includes(i.path) && i.path !== '/admin/dashboard');
                          if (children.length === 0) return null;
                          const open = openFamilies[fam.key] === undefined ? !!fam.defaultOpen : !!openFamilies[fam.key];
                          const famBadge = children.reduce((s, i) => s + (badgeForPath(i.path) || 0), 0);
                          const FamIcon = fam.icon;
                          return (
                            <div key={fam.key}>
                              <button
                                type="button"
                                onClick={() => toggleFamily(fam.key)}
                                aria-expanded={open}
                                aria-controls={`nav-family-panel-${fam.key}`}
                                className="w-full flex items-center justify-between px-3 min-h-[44px] rounded-xl text-sm text-white/85 hover:bg-white/8 transition-colors"
                                data-testid={`nav-family-${fam.key}`}
                              >
                                <div className="flex items-center gap-2.5">
                                  <FamIcon className="w-4 h-4" style={{ color: fam.color }} />
                                  <span className="font-semibold">{fam.label}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  {famBadge > 0 && !open && (
                                    <span className="text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1" style={{ background: '#C40050' }}>{famBadge}</span>
                                  )}
                                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: fam.color }} />
                                </div>
                              </button>
                              {open && (
                                <div id={`nav-family-panel-${fam.key}`} className="ml-3 pl-2 space-y-0.5 border-l-2" style={{ borderColor: `${fam.color}66` }} data-testid={`nav-family-items-${fam.key}`}>
                                  {children.map(c => renderNavItem(c))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {ungrouped.map(i => renderNavItem(i))}
                      </>
                    );
                  })()}
            </>
          );
        })()}
        {radioUrl && ['super_admin', 'store_admin'].includes(user?.role) && (          <a
            href={radioUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleNavClick}
            className="flex items-center justify-between px-3 py-2.5 rounded-xl transition-all text-sm text-white/70 hover:text-white hover:bg-white/8"
            data-testid="nav-radio-centrale"
          >
            <div className="flex items-center space-x-2.5">
              <Radio className="w-4 h-4 text-emerald-300" />
              <span>Radio centrale</span>
            </div>
            <span className="text-[9px] uppercase tracking-wide text-emerald-300/70 font-bold">Live</span>
          </a>
        )}
        <button
          type="button"
          onClick={toggleTheme}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all text-sm text-white/70 hover:text-white hover:bg-white/8"
          data-testid="theme-toggle"
        >
          <div className="flex items-center space-x-2.5">
            {uiTheme === 'dark' ? <Sun className="w-4 h-4 text-yellow-300" /> : <Moon className="w-4 h-4 text-indigo-300" />}
            <span>{uiTheme === 'dark' ? 'Mode clair' : 'Mode sombre'}</span>
          </div>
        </button>
      </nav>

      <div className="p-3 border-t border-white/10">
        <Link
          to="/admin/profile"
          className="mb-2 p-2.5 rounded-xl hover:bg-white/8 transition-colors cursor-pointer flex items-center gap-2.5"
          onClick={() => setSidebarOpen(false)}
          data-testid="profile-link"
        >
          <div className="ring-2 ring-white/25 rounded-full shrink-0" data-testid="sidebar-admin-avatar">
            <Avatar photoUrl={user?.profile_photo_url || user?.photo_url} firstName={user?.first_name} lastName={user?.last_name} size={38} testId="sidebar-admin-avatar-img" />
          </div>
          <div className="min-w-0">
          <p className="font-semibold text-sm text-white truncate">
            {user?.first_name} {user?.last_name}
          </p>
          <p className="text-xs" style={{ color: '#E7AF19' }}>
            {getRoleLabel(user?.role)}
          </p>
          <p className="text-[10px] text-white/40 mt-0.5">
            Voir mon profil
          </p>
          </div>
        </Link>
        {user?.role === 'super_admin' && !user?.is_gestionnaire && (
          <Link
            to="/admin/subscription"
            className="block mb-2 p-2.5 rounded-xl hover:bg-white/8 transition-colors cursor-pointer flex items-center gap-2"
            onClick={() => setSidebarOpen(false)}
            data-testid="subscription-link"
          >
            <CreditCard className="w-4 h-4 text-yellow-400" />
            <span className="text-sm text-yellow-400 font-semibold">Abonnement</span>
          </Link>
        )}
        {/* Enseigne login links for gerant & responsable */}
        {enseigneSlug && user?.role !== 'hyperadmin' && (
          <div className="mb-2 p-2.5 rounded-xl bg-white/5 border border-white/10">
            <p className="text-[10px] font-semibold text-white/50 uppercase tracking-wider flex items-center gap-1 mb-2">
              <Link2 className="w-3 h-3" /> Liens de connexion
            </p>
            <div className="space-y-1.5">
              <button
                onClick={() => { navigator.clipboard.writeText(`https://logyra.fr/api/v2/pwa-page/${enseigneSlug}`); toast.success('Lien employé copié !'); }}
                className="w-full flex items-center gap-2 text-[11px] text-white/70 hover:text-white transition-colors text-left group"
                data-testid="copy-enseigne-employee-url"
              >
                <Copy className="w-3 h-3 text-white/40 group-hover:text-green-400 shrink-0" />
                <span className="truncate">logyra.fr/{enseigneSlug}</span>
              </button>
              <button
                onClick={() => { navigator.clipboard.writeText(`https://logyra.fr/api/v2/pwa-page/${enseigneSlug}?mode=admin`); toast.success('Lien admin copié !'); }}
                className="w-full flex items-center gap-2 text-[11px] text-white/70 hover:text-white transition-colors text-left group"
                data-testid="copy-enseigne-admin-url"
              >
                <Copy className="w-3 h-3 text-white/40 group-hover:text-blue-400 shrink-0" />
                <span className="truncate">logyra.fr/{enseigneSlug}/admin</span>
              </button>
            </div>
          </div>
        )}
        {isSupported && (
          <Button
            variant="ghost"
            className={`w-full justify-start text-sm mb-1 rounded-xl ${isSubscribed ? 'text-white/80 hover:bg-white/8' : 'text-white/50 hover:bg-white/8'}`}
            onClick={togglePush}
            data-testid="push-toggle-btn"
          >
            {isSubscribed ? <Bell className="w-4 h-4 mr-2" /> : <BellOff className="w-4 h-4 mr-2" />}
            {isSubscribed ? 'Notif. push ON' : 'Activer notif. push'}
          </Button>
        )}
        <Button
          variant="ghost"
          className="w-full justify-start text-white/60 hover:text-white hover:bg-white/8 text-sm rounded-xl"
          onClick={handleLogout}
          data-testid="admin-logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Deconnexion
        </Button>
        <p className="text-[9px] text-white/20 text-center mt-2" data-testid="app-version">v2.5.0 · 09/10/2026</p>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Iter 447 : photo de profil visage obligatoire aussi pour les admins */}
      <ProfilePhotoPrompt />
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 text-white flex items-center justify-between px-4 py-3 shadow-lg" style={{ background: 'linear-gradient(135deg, #0a1b3d 0%, #4578A6 100%)' }}>
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-white/10 rounded-lg active:scale-95 transition-all"
            data-testid="mobile-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 hover:bg-white/10 rounded-lg"
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
        <SidebarLogo height="h-9" logoVersion={logoVersion} />
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Link to="/admin/profile" className="ring-2 ring-white/25 rounded-full shrink-0" data-testid="mobile-admin-avatar">
            <Avatar photoUrl={user?.profile_photo_url || user?.photo_url} firstName={user?.first_name} lastName={user?.last_name} size={30} testId="mobile-admin-avatar-img" />
          </Link>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
        w-60 h-screen text-white flex flex-col shrink-0
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ background: 'linear-gradient(180deg, #0a1b3d 0%, #102a52 100%)' }}>
        {sidebarContent}
      </div>

      {!sidebarOpen && <MobileBottomNav role={user?.role} onMore={() => setSidebarOpen(true)} allowed={(p) => navItems.some(i => i.path === p)} />}

      {/* Main content */}
      <div className="flex-1 min-w-0 overflow-auto" data-testid="admin-scroll-container">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 pt-16 lg:pt-6 pb-24 lg:pb-6">
          <HistoryGuards />
          <BackBar items={navItems} role={user?.role} />
          {planLoaded || user?.is_gestionnaire ? children : (
            <div className="py-12 space-y-4 text-center" data-testid="admin-access-verification">
              {planError ? <>
                <p role="alert" className="text-sm text-red-700" data-testid="admin-access-error">Impossible de vérifier les accès du magasin.</p>
                <Button variant="outline" onClick={retryPlan} data-testid="admin-access-retry">Réessayer</Button>
              </> : <p role="status" className="flex items-center justify-center gap-2 text-sm text-gray-600" data-testid="admin-access-loading"><Loader2 className="w-4 h-4 animate-spin" /> Vérification des accès…</p>}
            </div>
          )}
        </div>
      </div>

      {/* Subscription upgrade dialog */}
      {premiumDialogOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setPremiumDialogOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md mx-4 p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="premium-dialog">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                lockedTierInfo === 'diamond' ? 'bg-cyan-100' :
                lockedTierInfo === 'premium' ? 'bg-yellow-100' : 'bg-blue-100'
              }`}>
                {lockedTierInfo === 'diamond'
                  ? <Gem className="w-6 h-6 text-cyan-600" />
                  : <Crown className={`w-6 h-6 ${lockedTierInfo === 'premium' ? 'text-yellow-600' : 'text-blue-600'}`} />}
              </div>
              <div>
                <h3 className="font-heading text-lg text-gray-900">
                  {storePlan === 'none' ? 'Abonnement requis' :
                   lockedTierInfo === 'diamond' ? 'Fonctionnalite Diamond' : 'Fonctionnalite Premium'}
                </h3>
                <p className="text-xs text-gray-500">
                  {!['super_admin', 'hyperadmin'].includes(user?.role) ? 'Contactez votre gerant' :
                   storePlan === 'none' ? 'Souscrivez un abonnement pour acceder a cette fonctionnalite' :
                   lockedTierInfo === 'diamond' ? 'Passez a Diamond pour debloquer les modules operationnels' :
                   'Passez a Premium pour debloquer'}
                </p>
              </div>
            </div>
            {!['super_admin', 'hyperadmin'].includes(user?.role) ? (
              /* Iter 392 : les responsables/employes ne voient jamais les tarifs */
              <p className="font-body text-sm text-gray-600" data-testid="locked-dialog-no-pricing">
                {storePlan === 'none'
                  ? "Aucun abonnement n'est actif sur ce magasin. Contactez votre gerant pour l'activer."
                  : "Cette fonctionnalite n'est pas incluse dans l'abonnement actuel du magasin. Contactez votre gerant."}
              </p>
            ) : storePlan === 'none' ? (
              <>
                <p className="font-body text-sm text-gray-600">
                  Votre magasin n'a pas d'abonnement actif. Souscrivez un forfait pour debloquer les fonctionnalites.
                </p>
                <div className="space-y-2">
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm text-gray-800">Forfait Standard</p>
                        <p className="text-xs text-gray-500">Pointage, planning, conges, exports</p>
                      </div>
                      <p className="font-heading text-lg text-blue-700">39,99 €<span className="text-xs text-gray-400"> /mois</span></p>
                    </div>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm text-gray-800 flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-yellow-600" />Forfait Premium</p>
                        <p className="text-xs text-gray-500">Toutes les fonctionnalites RH</p>
                      </div>
                      <p className="font-heading text-lg text-yellow-700">49,99 €<span className="text-xs text-gray-400"> /mois</span></p>
                    </div>
                  </div>
                  <div className="p-3 bg-cyan-50 rounded-xl border border-cyan-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm text-gray-800 flex items-center gap-1"><Gem className="w-3.5 h-3.5 text-cyan-600" />Forfait Diamond</p>
                        <p className="text-xs text-gray-500">Premium + Taches / Livraisons / Commandes / Caisse</p>
                      </div>
                      <p className="font-heading text-lg text-cyan-700">59,99 €<span className="text-xs text-gray-400"> /mois</span></p>
                    </div>
                  </div>
                </div>
              </>
            ) : lockedTierInfo === 'diamond' ? (
              <>
                <p className="font-body text-sm text-gray-600">
                  Cette fonctionnalite est reservee aux abonnements <span className="font-semibold text-cyan-700">Diamond</span>.
                </p>
                <div className="p-3 bg-cyan-50 rounded-xl border border-cyan-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-gray-800 flex items-center gap-1"><Gem className="w-3.5 h-3.5 text-cyan-600" />Forfait Diamond</p>
                      <p className="text-xs text-gray-500">Taches / Livraisons / Commandes / Caisse + tout Premium</p>
                    </div>
                    <p className="font-heading text-lg text-cyan-700">59,99 €<span className="text-xs text-gray-400"> /mois</span></p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="font-body text-sm text-gray-600">
                  Cette fonctionnalite est reservee aux abonnements <span className="font-semibold text-yellow-700">Premium</span>.
                </p>
                <div className="p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-gray-800">Forfait Premium</p>
                      <p className="text-xs text-gray-500">Toutes les fonctionnalites incluses</p>
                    </div>
                    <p className="font-heading text-lg text-yellow-700">49,99 €<span className="text-xs text-gray-400"> /mois</span></p>
                  </div>
                </div>
              </>
            )}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setPremiumDialogOpen(false)} className="flex-1 px-4 py-2.5 border rounded-xl text-sm text-gray-600 hover:bg-gray-50">
                Fermer
              </button>
              {['super_admin', 'hyperadmin'].includes(user?.role) && (
                <button onClick={() => { setPremiumDialogOpen(false); navigate('/admin/subscription'); }}
                  className={`flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 ${
                    storePlan === 'none' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-yellow-500 hover:bg-yellow-600'
                  }`} data-testid="upgrade-premium-btn">
                  <CreditCard className="w-4 h-4" /> Voir les abonnements
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;

// Persistent layout wrapper for use with React Router Outlet
export const AdminLayoutOutlet = () => {
  return (
    <AdminLayout>
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
      <EquipmentSignPopup />
    </AdminLayout>
  );
};
